'use client';

import type { IntegrationAuthStatusProvider } from '@/lib/integrations/auth-status';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  integrationAuthStatusesQueryKey,
  integrationAuthStatusesRootQueryKey,
  useIntegrationAuthStatuses,
} from './use-integration-auth-statuses';

export type BrokenIntegrationProvider = IntegrationAuthStatusProvider;

type BrokenIntegrationConfig = {
  provider: BrokenIntegrationProvider;
  label: string;
};

export type BrokenIntegration = BrokenIntegrationConfig & {
  disconnect: () => Promise<void>;
  isDisconnecting: boolean;
};

const brokenIntegrationConfigs = [
  { provider: 'slack', label: 'Slack' },
  { provider: 'linear', label: 'Linear' },
  { provider: 'jira', label: 'Jira' },
  { provider: 'asana', label: 'Asana' },
  { provider: 'notion', label: 'Notion' },
  { provider: 'confluence', label: 'Confluence' },
] satisfies BrokenIntegrationConfig[];

export const brokenIntegrationsQueryKey = (installationId?: number | null) =>
  integrationAuthStatusesQueryKey(installationId);

const brokenIntegrationLabels = Object.fromEntries(
  brokenIntegrationConfigs.map(({ provider, label }) => [provider, label]),
) as Record<BrokenIntegrationProvider, string>;

function getDisconnectUrl(provider: BrokenIntegrationProvider, installationId?: number | null) {
  if (provider === 'slack') {
    return '/api/slack/connections/delete';
  }

  if (installationId == null) {
    throw new Error(`installationId is required to disconnect ${provider}`);
  }

  return `/api/${provider}/connections/delete?installationId=${installationId}`;
}

export function useBrokenIntegrations(installationId?: number | null) {
  const queryClient = useQueryClient();
  const queryKey = brokenIntegrationsQueryKey(installationId);
  const { statuses, isLoading } = useIntegrationAuthStatuses(installationId);

  const disconnectMutation = useMutation({
    mutationFn: async (provider: BrokenIntegrationProvider) => {
      const response = await fetch(getDisconnectUrl(provider, installationId), {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Failed to disconnect ${provider}`);
      }

      await response.json().catch(() => null);
    },
    onError: (error, provider) => {
      const label = brokenIntegrationLabels[provider];
      toast.error(error instanceof Error ? error.message : `Failed to disconnect ${label}`);
    },
    onSuccess: async (_data, provider) => {
      toast.success(`${brokenIntegrationLabels[provider]} disconnected`);
      await queryClient.invalidateQueries({
        queryKey: provider === 'slack' ? integrationAuthStatusesRootQueryKey : queryKey,
      });
    },
  });

  const disconnect = async (provider: BrokenIntegrationProvider) => {
    await disconnectMutation.mutateAsync(provider);
  };
  const disconnectingProvider =
    typeof disconnectMutation.variables === 'string' ? disconnectMutation.variables : null;

  const brokenIntegrations: BrokenIntegration[] = brokenIntegrationConfigs
    .filter(({ provider }) => statuses[provider] === 'needs_reauth')
    .map(({ provider, label }) => ({
      provider,
      label,
      disconnect: async () => disconnect(provider),
      isDisconnecting: disconnectMutation.isPending && disconnectingProvider === provider,
    }));

  return {
    brokenIntegrations,
    disconnect,
    isDisconnecting: disconnectMutation.isPending,
    isLoading,
  };
}
